import { Table, TablePagination } from "components/base";
import { TableProps } from "antd/lib/table";
import moment, { Moment } from "moment";
import { forwardRef, useEffect, useImperativeHandle } from "react";
import { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Card,
  Row,
  Col,
  Typography,
  Empty,
  Skeleton,
  Tag,
  Grid,
  Divider,
} from "antd";
import {
  CarOutlined,
  UserOutlined,
  ClockCircleOutlined,
  DashboardOutlined,
  ThunderboltOutlined,
  StarOutlined,
  RightOutlined,
} from "@ant-design/icons";

import { AppState } from "store";
import { parseTripsTable } from "utils/parsers/trips-parser";
import {
  getClients,
  setSelectedVehicle,
  setTripsSelectedPage,
} from "store/actions";
import { setClientFilters } from "store/actions/trips-actions";
import { ROUTES } from "../../../utils/constants";
import { useHistory } from "react-router-dom";

import "styles/components/tab-content/trips-content/trips-content.scss";

const { useBreakpoint } = Grid;
const { Text, Title } = Typography;

interface TripsContentProps {
  selectedDate: Moment | null;
  searchText: string;
}

// Desktop/Tablet Table Columns
const getColumns = (isTablet: boolean): TableProps<any>["columns"] => [
  {
    title: "Vehicle ID",
    dataIndex: "vehicle_id",
    width: isTablet ? 100 : 120,
    ellipsis: true,
    fixed: isTablet ? "left" : undefined,
  },
  {
    title: "Rider Name",
    dataIndex: "rider_name",
    width: isTablet ? 120 : 150,
    ellipsis: true,
  },
  {
    title: "Trip Start",
    dataIndex: "start_time",
    width: isTablet ? 150 : 190,
    ellipsis: true,
    
  },
  {
    title: "Trip End",
    dataIndex: "end_time",
    width: isTablet ? 150 : 190,
    ellipsis: true,
   
  },
  {
    title: "Distance (KMs)",
    dataIndex: "total_km",
    width: isTablet ? 100 : 130,
    align: "center",
  },
  {
    title: "SoC Used",
    dataIndex: "soc_used",
    width: isTablet ? 80 : 100,
    align: "center",
   
  },
  {
    title: "Available Batteries",
    dataIndex: "total_battery",
    width: isTablet ? 80 : 100,
    align: "center",

  },
  {
    title: "Rider Score",
    dataIndex: "score",
    width: isTablet ? 80 : 100,
    align: "right",
    
  },
];

const csvColumns = [
  { header: "Vehicle ID", key: "vehicle_id" },
  { header: "Rider Name", key: "rider_name" },
  { header: "Start Trip", key: "start_time" },
  { header: "End Trip", key: "end_time" },
  { header: "KMs", key: "total_km" },
  { header: "SOC used", key: "soc_used" },
];

const convertToCSV = (data: any[]) => {
  if (!data.length) return "";
  const headers = csvColumns.map((col) => col.header).join(",");
  const csvRows = [headers];

  data.forEach((row) => {
    const values = csvColumns.map((col) => {
      let value = row[col.key] || "";
      if (col.key === "start_time" || col.key === "end_time") {
        value = moment(value, "MMM DD, YYYY hh:mm A").isValid()
          ? moment(value, "MMM DD, YYYY hh:mm A").format("MMM DD, YYYY hh:mm A")
          : "Invalid Date";
      }
      return `"${value}"`;
    });
    csvRows.push(values.join(","));
  });

  return csvRows.join("\n");
};
//Trip card component for mobile view
interface TripCardProps {
  trip: any;
  onClick: () => void;
}

const TripCard = ({ trip, onClick }: TripCardProps) => {
  const screens = useBreakpoint();
  const isSmallMobile = !screens.sm;

  // Get score color
  const getScoreColor = (score: number) => {
    if (score >= 8) return "green";
    if (score >= 6) return "orange";
    return "red";
  };

  return (
    <Card className="trip-card" size="small" onClick={onClick}>
      {/* Card Header */}
      <div className="trip-card-header">
        <div className="vehicle-info">
          <div className="vehicle-id">
          
            <Text strong className="vehicle-text">
              {trip.vehicle_id || "N/A"}
            </Text>
          </div>
          <div className="rider-info">
            <UserOutlined className="rider-icon" />
            <Text className="rider-text">{trip.rider_name || "Unknown"}</Text>
          </div>
        </div>
        <div className="card-arrow">
          <RightOutlined />
        </div>
      </div>

      <Divider className="card-divider" />

      {/* Trip Times - Left Aligned */}
      <div className="trip-times">
        <div className="time-item">
          <Text type="secondary" className="time-label">
            Start
          </Text>
          <Text className="time-value">{trip.start_time || "—"}</Text>
        </div>
        {!isSmallMobile && <div className="time-separator">→</div>}
        <div className="time-item">
          <Text type="secondary" className="time-label">
            End
          </Text>
          <Text className="time-value">{trip.end_time || "—"}</Text>
        </div>
      </div>

      <Divider className="card-divider" />

      {/* Trip Stats */}
      <div className="trip-stats">
        <div className="stat-item">
          <DashboardOutlined className="stat-icon" />
          <div className="stat-content">
            <Text type="secondary" className="stat-label">
              Distance
            </Text>
            <Text strong className="stat-value">
              {trip.total_km || "0"} km
            </Text>
          </div>
        </div>

        <div className="stat-item">
          <ThunderboltOutlined className="stat-icon" />
          <div className="stat-content">
            <Text type="secondary" className="stat-label">
              SoC Used
            </Text>
            <Text strong className="stat-value">
              {trip.soc_used || "0%"}
            </Text>
          </div>
        </div>

        <div className="stat-item">
          <StarOutlined className="stat-icon" />
          <div className="stat-content">
            <Text type="secondary" className="stat-label">
              Score
            </Text>
            <Tag
              color={getScoreColor(Number(trip.score) || 0)}
              className="score-tag"
            >
              {trip.score || "—"}
            </Tag>
          </div>
        </div>
      </div>
    </Card>
  );
};

// Mobile Skeleton
const MobileSkeleton = () => (
  <div className="mobile-skeleton">
    {[1, 2, 3, 4].map((item) => (
      <Card key={item} className="trip-card">
        <Skeleton active paragraph={{ rows: 3 }} />
      </Card>
    ))}
  </div>
);

// Mobile View Component
interface MobileViewProps {
  data: any[];
  loading?: boolean;
  onCardClick: (trip: any) => void;
}

const MobileView = ({ data, loading, onCardClick }: MobileViewProps) => {
  if (loading) {
    return <MobileSkeleton />;
  }

  if (!data || data.length === 0) {
    return (
      <div className="empty-state">
        <Empty
          description="No trips found"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </div>
    );
  }

  return (
    <div className="mobile-trips-list">
      {data.map((trip, index) => (
        <TripCard
          key={trip.id || trip.key || index}
          trip={trip}
          onClick={() => onCardClick(trip)}
        />
      ))}
    </div>
  );
};

// Main Component
const TripsContent = forwardRef(
  ({ selectedDate, searchText }: TripsContentProps, ref) => {
    const dispatch = useDispatch();
    const history = useHistory();
    const screens = useBreakpoint();

    // Determine screen size
    const isMobile = !screens.md;
    const isTablet = Boolean(screens.md && !screens.lg);

    const { tripsCenter, downloadTrips, page, search } = useSelector<
      AppState,
      AppState["trips"]
    >((state) => state.trips);

    // TripsState does not include a `loading` property; use a safe fallback.
    const loading = false;

    const {
      vehicles: { clients },
    } = useSelector<AppState, AppState>((state) => state);

    const data = useMemo(
      () => parseTripsTable(tripsCenter),
      [tripsCenter]
    );

    const downloadData = useMemo(
      () => parseTripsTable(downloadTrips),
      [downloadTrips]
    );

    const { selectedPage, totalPages } = page;

    useEffect(() => {
      dispatch(getClients());
    }, [dispatch]);

    useEffect(() => {
      dispatch(setClientFilters(clients));
    }, [clients, dispatch]);

    const onBack = useCallback(() => {
      if (selectedPage > 1) {
        dispatch(setTripsSelectedPage(selectedPage - 1));
      }
    }, [dispatch, selectedPage]);

    const onNext = useCallback(() => {
      if (selectedPage < totalPages) {
        dispatch(setTripsSelectedPage(selectedPage + 1));
      }
    }, [dispatch, selectedPage, totalPages]);

    const resetCallback = useCallback(() => {
      dispatch(setTripsSelectedPage(1));
    }, [dispatch]);

    const handleDownloadCsv = () => {
      const csvString = convertToCSV(downloadData);
      const blob = new Blob([csvString], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.setAttribute("hidden", "");
      a.setAttribute("href", url);
      a.setAttribute("download", "SummaryOfTrips.csv");
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    };

    useImperativeHandle(ref, () => ({
      handleDownloadCsv,
    }));

    const handleRowClick = (trip: any) => {
      console.log("record : ", trip);
      history.push(`${ROUTES.trip_detail}/${trip?.id}`);
    };

    // Get columns based on screen size
    const columns = useMemo(() => getColumns(isTablet), [isTablet]);

    // Table scroll configuration
    const getScrollConfig = () => {
      if (isTablet) {
        return { y: "60vh", x: 700 };
      }
      return { y: "70vh" };
    };
    return (
      <div className="trips-content-container">
        {isMobile ? (
          <MobileView
            data={data}
            loading={loading}
            onCardClick={handleRowClick}
          />
        ) : (
          <Table
            className="trips-table"
            dataSource={data}
            columns={columns}
            onRow={(record) => ({
              onClick: () => handleRowClick(record),
            })}
            pagination={false}
            scroll={getScrollConfig()}
            size={isTablet ? "small" : "middle"}
            rowKey={(record) => record.id || record.key}
          />
        )}

        {/* Pagination */}
        <div className="pagination-wrapper">
          <TablePagination
            page={page}
            onBack={onBack}
            onNext={onNext}
            resetCallback={resetCallback}
          />
        </div>
      </div>
    );
  }
);

export default TripsContent;